import React from "react";
import "./TripDetails.css";
import { useState, useEffect } from "react";
import Modal from "../../components/Modal/Modal";
import AssignDriverAndCar from "../AssignDriverAndCar/AssignDriverAndCar";
import CloseDuty from "../CloseDuty/CloseDuty";
import ApiManager from "../../ApiManager/ApiManager";
import { useParams, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation } from "react-router-dom";
import BookingConfirmation from "../BookingConfirmation/BookingConfirmation";
import AllotedNotification from "../AllotedNotification/AllotedNotification";
import EditTrip from "../EditTrip/EditTrip";
import AddBookedTrip from "../AddNewTrip/AddBookedTrip";
import CompleteTrip from "../AddNewTrip/CompleteTrip";
import { getContrastColor } from "../../utils/GetContrastColor";
import TripRoute from "../../components/TripRoute";
import { Mail, Phone } from "lucide-react";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import TopNavbar from "../../components/TopNavbar/TopNavbar";

const TripDetails = ({ rowId: propRowId }) => {
  const location = useLocation();
  const { rowId: paramRowId } = useParams();
  const rowId = propRowId || paramRowId;
  const [tripData, setTripData] = useState(null);
  const [isModalOpen, setModalOpen] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editTrip, setEditTrip] = useState(false);
  const [bookTripDetails, setBookTripDetails] = useState(false);
  
  const {
    isLoading,
    getMessage,
    getProgress,
    withLoading
  } = useComponentLoading();

  const showToast = (message) => {
    toast.success(message, {
      position: "top-center",
      autoClose: 2000,
    });
  };

  const handleAssignSuccess = () => {
    setModalOpen(0); // Close modal
    showToast("Driver and car assigned successfully!");
    setRefreshKey((prev) => prev + 1); // Trigger refresh
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000); // Convert to milliseconds
    return date.toLocaleString(); // Format the date in a human-readable format
  };

  const navigate = useNavigate();

  const handleBookTripClick = async () => {
    setBookTripDetails(true);
  };

  const handleMarkDispatchedClick = async () => {
    try {
      await withLoading('dispatch', async (updateProgress) => {
        updateProgress(30, 'Marking trip as dispatched...');
        
        const params = new URLSearchParams({
          tripId: rowId,
        });
        const response = await ApiManager.markDispatched(params);
        if (!response.ok) throw new Error(await response.text());
        
        updateProgress(100, 'Trip marked as dispatched successfully!');
        showToast("Trip marked as dispatched successfully!");
        setRefreshKey((prev) => prev + 1);
      }, 'Marking trip as dispatched...');
    } catch (error) {
      toast.error(error.message || "Failed to dispatch the trip");
    }
  };

  

  const handleMarkBilledClick = () => {
    if (tripData) {
      const companyId = tripData.company.id;
      const tripIds = [tripData.tripId];

      navigate("/login/draft_invoice", {
        state: {
          companyId,
          tripIds,
        },
      });
    } else {
      console.error("No trip data available");
    }
  };

  const handleCancelTripClick = async () => {
    try {
      await withLoading('cancel', async (updateProgress) => {
        updateProgress(30, 'Cancelling trip...');
        
        const params = new URLSearchParams({
          tripId: rowId,
        });
        const response = await ApiManager.cancelTrip(params);
        if (!response.ok) {
          throw new Error("Failed to Cancel the trip");
        }
        
        updateProgress(100, 'Trip cancelled successfully!');
        showToast("Trip cancelled successfully!");
        setRefreshKey((prev) => prev + 1);
      }, 'Cancelling trip...');
    } catch (error) {
      console.error("Error in cancel the trip:", error);
      toast.error(error.message || "Failed to cancel the trip");
    }
  };

  const handleAssignDriverClick = () => {
    setModalOpen(1);
  };

  const handleTripEdit = () => {
    setEditTrip(true);
  };

  const handleSendDriverAllotedNotification = () => {
    setModalOpen(4);
  };

  const handleSendBookingConfirmation = () => {
    setModalOpen(3);
  };

  const completeTripClicked = () => {
    setModalOpen(2);
  };

  useEffect(() => {
    // Check if a toast message should be displayed when the component mounts
    if (location.state?.showToast) {
      showToast(
        location.state.toastMessage || "Action completed successfully!"
      );
      // Clear the state after showing the toast (optional)
      window.history.replaceState({}, document.title);
    }

    // Handle actions passed from ShowTrips
    if (location.state?.action) {
      const action = location.state.action;
      switch (action) {
        case 'book':
          handleBookTripClick();
          break;
        case 'cancel':
          handleCancelTripClick();
          break;
        case 'assign':
          handleAssignDriverClick();
          break;
        case 'dispatch':
          handleMarkDispatchedClick();
          break;
        case 'booking_confirmation':
          handleSendBookingConfirmation();
          break;
        case 'driver_alloted_notification':
          handleSendDriverAllotedNotification();
          break;
        case 'complete':
          completeTripClicked();
          break;
        default:
          break;
      }
      // Clear the action from state after handling
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await withLoading('fetchTripDetails', async (updateProgress) => {
          updateProgress(10, 'Fetching trip details...');
          
          const response = await ApiManager.getTripById(rowId);
          if (!response.ok) {
            throw new Error("Failed to fetch trip details");
          }
          
          updateProgress(50, 'Processing trip data...');
          const data = await response.json();
          
          updateProgress(90, 'Updating UI...');
          if (data) {
            setTripData(data.trip);
          }
          
          updateProgress(100, 'Trip details loaded successfully');
        }, 'Loading trip details...');
      } catch (error) {
        console.error("Error fetching trip details:", error);
        toast.error("Failed to load trip details");
      }
    };

    fetchData();
  }, [rowId, refreshKey]);

  const bookingInfo = {
    id: `#${tripData?.tripId || ""}`,
    status: tripData?.tripStatus || "",
    startDate: formatTimestamp(tripData?.scheduledStartTime || ""),
    endDate: formatTimestamp(tripData?.scheduledEndTime || ""),
    reportingTime: formatTimestamp(tripData?.reportingTime || ""),
    fromCity: tripData?.fromCity || "",
    toCity: tripData?.toCity || "",
    tripType: tripData?.tripType?.name || "N/A",
    carType: tripData?.carType || "N/A",
    extraKmRate: tripData?.charges?.rates?.extra_km_rates || 0,
    extraHrRate: tripData?.charges?.rates?.extra_hr_rates || 0,
    passengers: tripData?.passengers || [],
  };

  if (isLoading('fetchTripDetails')) {
    return (
      <div className="trip-details-loading">
        <LoadingSpinner 
          overlay={false}
          message={getMessage('fetchTripDetails')}
          progress={getProgress('fetchTripDetails')}
          showProgress={true}
        />
      </div>
    );
  }

  if (!tripData) {
    return <div>No trip data available</div>;
  }

  const renderActions = () => {
    switch (bookingInfo.status) {
      case "CREATED":
        return (
          <>
            <button className="custom-button" onClick={handleTripEdit}>
              Edit Trip
            </button>
            <button className="custom-button" onClick={handleBookTripClick}>
              Book Trip
            </button>
            <button className="custom-button" onClick={handleCancelTripClick}>
              Cancel Trip
            </button>
          </>
        );
        case "BILLED":
        return (
          <>
            <button className="custom-button" onClick={handleCancelTripClick}>
              Cancel Trip
            </button>
          </>
        );
      case "BOOKED":
        return (
          <>
            <button className="custom-button" onClick={handleTripEdit}>
              Edit Trip
            </button>
            <button className="custom-button" onClick={handleAssignDriverClick}>
              Assign Driver
            </button>
            <button className="custom-button" onClick={handleCancelTripClick}>
              Cancel Trip
            </button>
            <button
              className="custom-button"
              onClick={handleSendBookingConfirmation}
            >
              Send Booking Confirmation
            </button>
          </>
        );
      case "ALLOTTED":
        return (
          <>
            <button className="custom-button" onClick={handleTripEdit}>
              Edit Trip
            </button>
            {/* <button className="custom-button" onClick={handleMarkDispatchedClick}>
              Mark Dispatched
            </button> */}
            <button
              className="custom-button"
              onClick={completeTripClicked}
              disabled={tripData?.driverTrip?.tripStatus != "ENDED"}
            >
              Complete Trip
            </button>

            <button className="custom-button" onClick={handleCancelTripClick}>
              Cancel Trip
            </button>
            <button
              className="custom-button"
              onClick={handleSendBookingConfirmation}
            >
              Send Booking Confirmation
            </button>
            <button
              className="custom-button"
              onClick={handleSendDriverAllotedNotification}
            >
              Send Driver Alloted Notification
            </button>
          </>
        );
      case "COMPLETED":
        return (
          <>
            <button className="custom-button" onClick={handleTripEdit}>
              Edit Trip
            </button>
            <button className="custom-button" onClick={handleCancelTripClick}>
              Cancel Trip
            </button>
            <button className="custom-button" onClick={handleMarkBilledClick}>
              Generate Invoice
            </button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="trip-details">
        <ToastContainer />
        
        {editTrip && tripData && (
          <div className="edit-trip-container">
            <EditTrip 
              tripInfo={tripData} 
              setEditTrip={(value) => {
                setEditTrip(value);
                if (!value) setRefreshKey(prev => prev + 1); // Refresh data if edit is closed
              }} 
            />
          </div>
        )}
        
        {bookTripDetails && tripData && (
          <AddBookedTrip
            tripInfo={tripData}
            setEditTrip={setBookTripDetails}
            setRefreshKey={setRefreshKey}
          />
        )}

        {!editTrip && !bookTripDetails && tripData && (
          <div>
            <TopNavbar title="Trip Details" actions={renderActions()} />
            <div className="info-item">
              <div className="selectedLabels">
                {(tripData.labels || []).map((label) => {
                  const backgroundColor = label.color;
                  const textColor = getContrastColor(backgroundColor);
                  return (
                    <div
                      key={label.id}
                      className="selectedLabel"
                      style={{ backgroundColor: label.color, color: textColor }}
                    >
                      {label.name}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="booking-section">
              <h3>Passengers</h3>
              <div className="passenger-card-container">
                {tripData?.passengers?.length > 0 ? (
                  tripData?.passengers?.map((passenger, index) => (
                    <div key={index} className="passenger-card">
                      <h4>{passenger.name || "N/A"}</h4>
                      <p className="email-container">
                        <Phone size={"1em"} className="email-icon" />
                        {passenger.phone || "No contact provided"}
                      </p>
                      <p className="email-container">
                        <Mail size={"1em"} className="email-icon" />
                        {passenger.email || "No email provided"}
                      </p>
                    </div>
                  ))
                ) : (
                  <p>No passengers listed</p>
                )}
              </div>
            </div>

            <div className="booking-section">
              <h3>Trip Route</h3>
              <TripRoute
                reportingAddress={tripData?.reportingAddress}
                dropAddress={tripData?.dropAddress}
              />
            </div>

            <div className="booking-section">
              <h3>Trip Details</h3>
              <div className="info-card">
                <div className="info-grid">
                  <div className="info-item">
                    <strong>Trip Id</strong>
                    <p>{tripData?.tripId || "N/A"}</p>
                  </div>

                  <div className="info-item">
                    <strong>Status</strong>
                    <p>{tripData?.tripStatus || "N/A"}</p>
                  </div>

                  <div className="info-item">
                    <strong>Company</strong>
                    <p>{tripData?.company?.name || "N/A"}</p>
                  </div>

                  <div className="info-item">
                    <strong>Start Date</strong>
                    <p>{tripData?.scheduledStartTime || "N/A"}</p>
                  </div>

                  <div className="info-item">
                    <strong>End Date</strong>
                    <p>{tripData?.scheduledEndTime || "N/A"}</p>
                  </div>

                  <div className="info-item">
                    <strong>From City</strong>
                    <p>{tripData?.fromCity || "N/A"}</p>
                  </div>

                  <div className="info-item">
                    <strong>To City</strong>
                    <p>{tripData?.toCity || "N/A"}</p>
                  </div>

                  <div className="info-item">
                    <strong>Trip Type</strong>
                    <p>{tripData?.tripType?.name || "N/A"}</p>
                  </div>
                  <div className="info-item">
                    <strong>Contract Auto Switch</strong>
                    <p>{tripData?.contractAutoSwitchEnabled ? 'On' : 'Off'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="booking-section">
              <h3>Driver Details</h3>
              <div className="info-card">
                <div className="info-grid">
                  <div className="info-item">
                    <strong>Name</strong>
                    <p>
                      {tripData?.driverTrip?.driver?.name || "Not assigned yet"}
                    </p>
                  </div>
                  <div className="info-item">
                    <strong>Contact</strong>
                    <p>{tripData?.driverTrip?.driver?.phone || "N/A"}</p>
                  </div>
                  <div className="info-item">
                    <strong>Email</strong>
                    <p>{tripData?.driverTrip?.driver?.email || "N/A"}</p>
                  </div>
                  <div className="info-item">
                    <strong>Status</strong>
                    <p>{tripData?.driverTrip?.tripStatus || "N/A"}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="booking-section">
              <h3>Vehicle & Charges</h3>
              <div className="info-card">
                <div className="info-grid">
                  <div className="info-item">
                    <strong>Vehicle Name</strong>
                    <p>
                      {tripData?.driverTrip?.car?.vehicleName ||
                        "Not assigned yet"}
                    </p>
                  </div>
                  <div className="info-item">
                    <strong>Vehicle Number</strong>
                    <p>{tripData?.driverTrip?.car?.vehicleNo || "N/A"}</p>
                  </div>
                  <div className="info-item">
                    <strong>Vehicle Type</strong>
                    <p>{tripData?.driverTrip?.car?.carTypeName || "N/A"}</p>
                  </div>
                  <div className="info-item">
                    <strong>Per Extra KM rate</strong>
                    <p>{tripData?.charges?.rates?.extra_km_rates || 0}</p>
                  </div>
                  <div className="info-item">
                    <strong>Per Extra Hr rate</strong>
                    <p>{tripData?.charges?.rates?.extra_hr_rates || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Section */}
        <Modal isOpen={isModalOpen === 1} onClose={() => setModalOpen(0)} title="Assign Driver and Car">
          <div>
            <AssignDriverAndCar tripId={rowId} onSuccess={handleAssignSuccess} />
          </div>
        </Modal>

        <Modal isOpen={isModalOpen === 2} onClose={() => setModalOpen(0)} title="Complete Trip">
          <div>
            <CompleteTrip
              tripInfo={tripData}
              setEditTrip={setModalOpen}
              setRefreshKey={setRefreshKey}
            />
          </div>
        </Modal>

        <Modal isOpen={isModalOpen === 3} onClose={() => setModalOpen(0)} title="Send Booking Confirmation">
            <BookingConfirmation tripId={rowId} />
        </Modal>

        <Modal isOpen={isModalOpen === 4} onClose={() => setModalOpen(0)} title="Send Driver Alloted Notification">
          <div>
            <AllotedNotification tripId={rowId} />
          </div>
        </Modal>
      </div>
    </>
  );
};

export default TripDetails;
